import { captureException, addBreadcrumb } from './sentry';
import { showError } from './messageService';

/**
 * Global error handler for uncaught errors
 */
export const setupGlobalErrorHandlers = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);

    addBreadcrumb({
      category: 'promise',
      message: 'Unhandled promise rejection',
      level: 'error',
      data: {
        reason: event.reason?.toString(),
      },
    });

    captureException(event.reason || new Error('Unhandled promise rejection'));

    // Prevent default browser error handling
    event.preventDefault();
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);

    addBreadcrumb({
      category: 'error',
      message: 'Global error',
      level: 'error',
      data: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });

    if (event.error) {
      captureException(event.error);
    }
  });
};

/**
 * Handle API errors with user-friendly messages
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @param {boolean} showToUser - Whether to show error to user
 */
export const handleApiError = (error, context = 'API', showToUser = true) => {
  console.error(`${context} error:`, error);

  // Add breadcrumb for debugging
  addBreadcrumb({
    category: 'api',
    message: `${context} error`,
    level: 'error',
    data: {
      error: error?.message,
      status: error?.status,
      context,
    },
  });

  // Capture in Sentry
  captureException(error, {
    tags: {
      errorType: 'api',
      context,
    },
  });

  // Show user-friendly message
  if (showToUser) {
    const message = getUserFriendlyErrorMessage(error);
    showError(message);
  }

  return error;
};

/**
 * Get user-friendly error message
 * @param {Error} error - Error object
 * @returns {string} User-friendly message
 */
const getUserFriendlyErrorMessage = (error) => {
  // Check for network errors
  if (!navigator.onLine) {
    return 'No internet connection. Please check your network.';
  }

  // Check for specific error types
  if (error?.message?.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  if (error?.message?.includes('Network')) {
    return 'Network error. Please check your connection.';
  }

  // Check for HTTP status codes
  const status = error?.status || error?.response?.status;

  switch (status) {
    case 400:
      return error?.data?.message || 'Invalid request. Please check your input.';
    case 401:
      return 'Session expired. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 422:
      return error?.data?.message || 'Validation error. Please check your input.';
    case 500:
      return 'Server error. Please try again later.';
    case 503:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return error?.data?.message || error?.message || 'An unexpected error occurred.';
  }
};

/**
 * Handle async function with error handling
 * @param {Function} fn - Async function to execute
 * @param {string} context - Context for error reporting
 * @returns {Promise} Result or error
 */
export const withErrorHandling = async (fn, context = 'Operation') => {
  try {
    return await fn();
  } catch (error) {
    handleApiError(error, context);
    throw error;
  }
};

export default {
  setupGlobalErrorHandlers,
  handleApiError,
  withErrorHandling,
};
